import { clamp01, lerp, snapToPixel } from '../util/math.js';
import { isScrimmageSnap, playChance } from './helpers.js';
import { liveTempoForSpeed } from './speed.js';
import { teamPlayersAtPosition } from '../../team-roster.js';
import {
  buildFieldGoalUnits,
  buildKickoffUnits,
  buildOnsideUnits,
  buildPuntUnits,
  fieldGoalAssignments,
  kickoffAssignments,
  onsideAssignments,
  puntAssignments,
} from '../../arcade/special-teams-personnel.js';

function rosterList(team: any): any[] {
  return team?.roster || team?.players || [];
}

function uniquePlayers(players: any[]): any[] {
  const seen = new Set();
  return players.filter((player, index) => {
    if (!player) {
      return false;
    }
    const key = playerIdentityKey(player) || `${player.position || 'UNK'}:${player.number || index}:${player.name || ''}:${index}`;
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}

function topPlayers(team: any, position: string, count = 1): any[] {
  return uniquePlayers(teamPlayersAtPosition(team, position, count));
}

function starterPlayer(team: any, position: string): any {
  return topPlayers(team, position, 1)[0] || null;
}

function findPlayerById(team: any, playerId: any): any {
  if (!team || !playerId) {
    return null;
  }
  return rosterList(team).find((player: any) => player.id === playerId) || null;
}

function findPlayerByName(team: any, name: any): any {
  if (!team || !name) {
    return null;
  }
  const normalized = String(name).trim().toLowerCase();
  return rosterList(team).find((player: any) => String(player?.name || '').trim().toLowerCase() === normalized) || null;
}

function resolveParticipant(team: any, participants: any, idKey: string, nameKey: string): any {
  return findPlayerById(team, participants?.[idKey])
    || findPlayerByName(team, participants?.[nameKey]);
}

function playerIdentityKey(player: any): string | null {
  if (!player) {
    return null;
  }
  if (player.playerId) {
    return String(player.playerId);
  }
  if (player.id) {
    return String(player.id);
  }
  if (player.name) {
    return `${player.name}:${player.number || ''}:${player.position || ''}`;
  }
  return null;
}

const SCRIMMAGE_ROLE_PRIORITY = {
  offense: ['QB', 'RB', 'LT', 'LG', 'C', 'RG', 'RT', 'WR1', 'WR2', 'SLOT', 'TE'],
  defense: ['DE1', 'DE2', 'DT1', 'DT2', 'LB1', 'LB2', 'CB1', 'CB2', 'S1', 'S2', 'LB3'],
} as const;

const SCRIMMAGE_ROLE_POSITION_PREFERENCES: Record<string, string[]> = {
  QB: ['QB'],
  RB: ['RB', 'FB'],
  LT: ['OT', 'OG'],
  LG: ['OG', 'OT'],
  C: ['C', 'OG'],
  RG: ['OG', 'OT'],
  RT: ['OT', 'OG'],
  TE: ['TE', 'WR', 'FB'],
  WR1: ['WR', 'TE'],
  WR2: ['WR', 'TE'],
  SLOT: ['WR', 'TE', 'RB', 'FB'],
  DE1: ['DE', 'DT', 'LB'],
  DE2: ['DE', 'DT', 'LB'],
  DT1: ['DT', 'DE'],
  DT2: ['DT', 'DE'],
  LB1: ['LB', 'S', 'CB'],
  LB2: ['LB', 'S', 'CB'],
  LB3: ['LB', 'CB', 'S'],
  CB1: ['CB', 'S'],
  CB2: ['CB', 'S'],
  S1: ['S', 'CB'],
  S2: ['S', 'CB'],
};

function distinctScrimmageAssignments(
  assignments: Record<string, any>,
  team: any,
  side: keyof typeof SCRIMMAGE_ROLE_PRIORITY,
): Record<string, any> {
  const roles = SCRIMMAGE_ROLE_PRIORITY[side];
  const rosterPool = uniquePlayers(rosterList(team));
  const used = new Set<string>();
  const resolved: Record<string, any> = { ...assignments };

  const unusedPlayer = (preferredPositions: string[]): any =>
    rosterPool.find((player) => {
      const identity = playerIdentityKey(player);
      return identity && !used.has(identity) && preferredPositions.includes(String(player.position || '').toUpperCase());
    })
    || rosterPool.find((player) => {
      const identity = playerIdentityKey(player);
      return identity && !used.has(identity);
    })
    || null;

  for (const role of roles) {
    let player = assignments[role] || null;
    const identity = playerIdentityKey(player);
    if (!identity || used.has(identity)) {
      player = unusedPlayer(SCRIMMAGE_ROLE_POSITION_PREFERENCES[role] || []);
    }

    const resolvedIdentity = playerIdentityKey(player);
    if (resolvedIdentity) {
      used.add(resolvedIdentity);
    }
    resolved[role] = player;
  }

  return resolved;
}

function distinctPlayers(seeds: any[], pool: any[]): any[] {
  const used = new Set();
  const result: any[] = [];

  function pushPlayer(player: any) {
    if (!player) {
      return false;
    }
    const key = playerIdentityKey(player) || `${player.position || 'UNK'}:${player.number || result.length}`;
    if (used.has(key)) {
      return false;
    }
    used.add(key);
    result.push(player);
    return true;
  }

  seeds.forEach(pushPlayer);
  pool.forEach((player) => {
    if (result.length < seeds.length) {
      pushPlayer(player);
    }
  });

  while (result.length < seeds.length) {
    result.push(result[result.length - 1] || pool[0] || null);
  }

  return result;
}

function chooseRotatedPlayer(pool: any[], backupIndex: number, play: any, salt: string, chance: number): any {
  if (!pool.length) {
    return null;
  }
  if (pool[backupIndex] && playChance(play, salt, chance)) {
    return pool[backupIndex];
  }
  return pool[Math.min(backupIndex - 1, pool.length - 1)] || pool[0];
}

function preferredReceiverIndex(play: any): number {
  if (play?.playId === 'screen-left' || play?.playId === 'mesh-cross') {
    return 2;
  }
  if (play?.playId === 'quick-slant' || play?.playId === 'flood-shot') {
    return 1;
  }
  return 0;
}

function buildOffensePersonnel(play: any, team: any): Record<string, any> {
  const participants = play?.participants || {};
  const personnelGroup = play?.personnelGroup || '11';

  const qbs = topPlayers(team, 'QB', 2);
  const rbs = topPlayers(team, 'RB', 3);
  const fbs = topPlayers(team, 'FB', 2);
  const wrs = topPlayers(team, 'WR', 5);
  const tes = topPlayers(team, 'TE', 3);
  const ots = topPlayers(team, 'OT', 3);
  const ogs = topPlayers(team, 'OG', 3);
  const centers = topPlayers(team, 'C', 2);

  const runner = resolveParticipant(team, participants, 'runnerId', 'runnerName');
  const target = resolveParticipant(team, participants, 'targetId', 'targetName');
  const passer = resolveParticipant(team, participants, 'passerId', 'passerName');

  const qb = passer || team?.depthChart?.qb || starterPlayer(team, 'QB') || qbs[0] || null;
  const rb = (runner && ['RB', 'FB'].includes(runner.position)) ? runner
    : (target && ['RB', 'FB'].includes(target.position)) ? target
      : chooseRotatedPlayer(rbs, 1, play, 'offense-rb-sub', play?.family === 'run' ? 0.24 : 0.12)
        || fbs[0]
        || rbs[0]
        || qb;
  const te = (target?.position === 'TE' ? target : null)
    || (runner?.position === 'TE' ? runner : null)
    || chooseRotatedPlayer(tes, 1, play, 'offense-te-sub', play?.family === 'run' || play?.playId === 'play-action-post' ? 0.3 : 0.14)
    || tes[0]
    || wrs[0]
    || qb;

  const receiverPool = uniquePlayers([
    ...(target?.position === 'WR' ? [target] : []),
    ...(runner?.position === 'WR' ? [runner] : []),
    ...wrs,
  ]);
  const receiverSeeds = [
    receiverPool[0],
    receiverPool[1] || receiverPool[0],
    receiverPool[2] || receiverPool[1] || receiverPool[0],
  ];
  if (play?.family === 'run' && receiverPool[3] && playChance(play, 'offense-slot-sub', 0.38)) {
    receiverSeeds[2] = receiverPool[3];
  }
  if (play?.playId === 'screen-left' && receiverPool[3] && playChance(play, 'offense-wr2-sub', 0.28)) {
    receiverSeeds[1] = receiverPool[3];
  }
  const featuredReceiver = target?.position === 'WR' ? target : (runner?.position === 'WR' ? runner : null);
  if (featuredReceiver) {
    receiverSeeds[preferredReceiverIndex(play)] = featuredReceiver;
  }
  const [wr1, wr2, slot] = distinctPlayers(receiverSeeds, receiverPool);
  const extraTightEnd = tes[1] || te;
  const extraWide = receiverPool[3] || receiverPool[2] || wr2 || wr1;
  const fullback = fbs[0] || rbs[1] || rb;

  let teAssignment = te;
  let slotAssignment = slot;
  if (personnelGroup === '10') {
    teAssignment = extraWide || slot || wr2 || wr1;
    slotAssignment = slot || wr2 || wr1;
  } else if (personnelGroup === '12') {
    slotAssignment = extraTightEnd || slot || te;
  } else if (personnelGroup === '21') {
    slotAssignment = fullback || extraTightEnd || slot || rb;
  }

  return {
    LT: ots[0] || null,
    LG: ogs[0] || null,
    C: centers[0] || null,
    RG: ogs[1] || ogs[0] || null,
    RT: ots[1] || ots[0] || null,
    QB: qb,
    RB: rb,
    TE: teAssignment,
    WR1: wr1,
    WR2: wr2,
    SLOT: slotAssignment,
  };
}

function buildDefensePersonnel(play: any, team: any): Record<string, any> {
  const coverageShell = play?.coverageShell || 'single-high';
  const boxCount = Math.max(6, Math.min(8, Number(play?.boxCount || 7)));
  const des = topPlayers(team, 'DE', 4);
  const dts = topPlayers(team, 'DT', 4);
  const lbs = topPlayers(team, 'LB', 5);
  const cbs = topPlayers(team, 'CB', 4);
  const safeties = topPlayers(team, 'S', 3);
  const nickelPool = uniquePlayers([cbs[2], safeties[2], cbs[1], safeties[1], lbs[2]].filter(Boolean));
  const lightBox = boxCount <= 6 || coverageShell === 'two-high' || coverageShell === 'quarters';

  return {
    DE1: des[0] || null,
    DT1: dts[0] || null,
    DT2: chooseRotatedPlayer(dts, 2, play, 'defense-dt-sub', play?.family === 'run' || boxCount >= 7 ? 0.24 : 0.12) || dts[1] || dts[0] || null,
    DE2: chooseRotatedPlayer(des, 2, play, 'defense-de-sub', play?.family === 'pass' || play?.pressureLook === 'edge' ? 0.24 : 0.14) || des[1] || des[0] || null,
    LB1: lbs[0] || null,
    LB2: lbs[1] || lbs[0] || null,
    LB3: lightBox
      ? chooseRotatedPlayer(nickelPool, 1, play, 'defense-nickel-sub', 0.32) || nickelPool[0] || lbs[2] || lbs[1] || lbs[0] || null
      : chooseRotatedPlayer(lbs, 3, play, 'defense-lb-sub', play?.family === 'pass' || (play?.distance || 0) >= 8 ? 0.36 : 0.22) || lbs[2] || lbs[1] || lbs[0] || null,
    CB1: cbs[0] || null,
    CB2: chooseRotatedPlayer(cbs, 2, play, 'defense-cb-sub', play?.family === 'pass' ? 0.28 : 0.12) || cbs[1] || cbs[0] || null,
    S1: safeties[0] || null,
    S2: chooseRotatedPlayer(safeties, 2, play, 'defense-s-sub', coverageShell === 'single-high' ? 0.18 : 0.28) || safeties[1] || safeties[0] || null,
  };
}

function attachPlayer(fieldPlayer: any, rosterPlayer: any): any {
  if (!rosterPlayer) {
    return fieldPlayer;
  }
  const speedRating = Number.isFinite(rosterPlayer?.ratings?.speed)
    ? rosterPlayer.ratings.speed
    : (fieldPlayer?.speedRating || 75);
  return {
    ...fieldPlayer,
    playerId: rosterPlayer.id,
    name: rosterPlayer.name,
    number: rosterPlayer.number,
    position: rosterPlayer.position,
    appearance: rosterPlayer.appearance,
    skinColor: rosterPlayer?.appearance?.skinColor || rosterPlayer?.skinColor,
    hairColor: rosterPlayer?.appearance?.hairColor || rosterPlayer?.hairColor,
    speedRating,
    liveTempo: liveTempoForSpeed(speedRating),
  };
}

export function assignPersonnelToFormation(play: any, offense: any, defense: any, formation: { offense: any[]; defense: any[] }) {
  let specialTeamsAssignments: { offense: Record<string, any>; defense: Record<string, any> } | null = null;
  if (play?.playId === 'kickoff') {
    const onsideKick = play?.arcadeReplay?.playCall === 'onside-kick'
      || String(play?.kickoffOutcome || '').startsWith('onside-');
    specialTeamsAssignments = onsideKick
      ? onsideAssignments(buildOnsideUnits(offense, defense))
      : kickoffAssignments(buildKickoffUnits(offense, defense));
  } else if (play?.playId === 'punt') {
    specialTeamsAssignments = puntAssignments(buildPuntUnits(offense, defense));
  } else if (play?.playId === 'field-goal' || play?.playId === 'pat') {
    specialTeamsAssignments = fieldGoalAssignments(buildFieldGoalUnits(offense, defense));
  }

  if (specialTeamsAssignments) {
    return {
      offense: formation.offense.map((player) => attachPlayer(player, specialTeamsAssignments!.offense[player.role])),
      defense: formation.defense.map((player) => attachPlayer(player, specialTeamsAssignments!.defense[player.role])),
    };
  }

  if (!isScrimmageSnap(play)) {
    return formation;
  }

  const offenseAssignments = distinctScrimmageAssignments(buildOffensePersonnel(play, offense), offense, 'offense');
  const defenseAssignments = distinctScrimmageAssignments(buildDefensePersonnel(play, defense), defense, 'defense');

  return {
    offense: formation.offense.map((player) => attachPlayer(player, offenseAssignments[player.role])),
    defense: formation.defense.map((player) => attachPlayer(player, defenseAssignments[player.role])),
  };
}

function interpolatePlayer(start: any, end: any, amount: number, template: any): any {
  const progress = clamp01(amount);
  const x = snapToPixel(lerp(start?.x ?? end?.x ?? 0, end?.x ?? start?.x ?? 0, progress));
  const y = snapToPixel(lerp(start?.y ?? end?.y ?? 0, end?.y ?? start?.y ?? 0, progress));
  const moving = progress < 1 && (Math.abs((start?.x ?? x) - (end?.x ?? x)) > 0 || Math.abs((start?.y ?? y) - (end?.y ?? y)) > 0);
  return {
    ...(template || end || start),
    x,
    y,
    moving,
  };
}

function findBenchPlayer(players: any[], benchPlayers: any[], index: number): any {
  const identity = playerIdentityKey(players[index]);
  if (identity) {
    const match = benchPlayers.find((player) => playerIdentityKey(player) === identity);
    if (match) {
      return match;
    }
  }
  return benchPlayers[index] || benchPlayers.find((player) => player?.role === players[index]?.role) || players[index];
}

export function transitionPersonnelPlayers(
  sourcePlayers: any[],
  targetPlayers: any[],
  sourceBench: any[],
  targetBench: any[],
  amount: number
): any[] {
  const result: any[] = [];
  const matchedSource = new Set<number>();
  const usedBenchStarts = new Set<number>();

  targetPlayers.forEach((targetPlayer, targetIndex) => {
    const identity = playerIdentityKey(targetPlayer);
    let sourceIndex = -1;

    if (identity) {
      sourceIndex = sourcePlayers.findIndex((player, index) => !matchedSource.has(index) && playerIdentityKey(player) === identity);
    }
    if (sourceIndex < 0 && !identity) {
      sourceIndex = sourcePlayers.findIndex((player, index) => !matchedSource.has(index) && player?.role === targetPlayer?.role);
    }

    if (sourceIndex >= 0) {
      matchedSource.add(sourceIndex);
      result.push(interpolatePlayer(sourcePlayers[sourceIndex], targetPlayer, amount, targetPlayer));
      return;
    }

    let benchIndex = -1;
    if (identity) {
      benchIndex = targetBench.findIndex((player, index) => !usedBenchStarts.has(index) && playerIdentityKey(player) === identity);
    }
    if (benchIndex < 0) {
      benchIndex = targetBench.findIndex((player, index) => !usedBenchStarts.has(index) && player?.role === targetPlayer?.role);
    }
    if (benchIndex >= 0) {
      usedBenchStarts.add(benchIndex);
    }
    result.push(interpolatePlayer(targetBench[benchIndex] || targetPlayer, targetPlayer, amount, targetPlayer));
  });

  sourcePlayers.forEach((sourcePlayer, sourceIndex) => {
    if (matchedSource.has(sourceIndex)) {
      return;
    }
    result.push(interpolatePlayer(
      sourcePlayer,
      findBenchPlayer(sourcePlayers, sourceBench, sourceIndex),
      amount,
      sourcePlayer
    ));
  });

  return result;
}
